import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, CheckCircle, Shield, Clock, FileText, AlertTriangle, DollarSign, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import CTABanner from "@/components/CTABanner";
import SEO from "@/components/SEO";

export default function InspectionsForBuyers() {
  const buyerBenefits = [
    {
      icon: Shield,
      title: "Avoid Costly Surprises",
      description: "Identify potential issues before they become expensive problems. Our thorough inspections reveal what you can't see during an open home."
    },
    {
      icon: DollarSign,
      title: "Negotiate with Confidence",
      description: "Use our detailed report to negotiate a fair price or request repairs before settlement. Knowledge is power in property negotiations."
    },
    {
      icon: FileText,
      title: "Clear, Visual Reports",
      description: "Receive easy-to-understand reports with photos documenting any issues. No confusing jargon — just clear information you can act on."
    },
    {
      icon: Clock,
      title: "Fast 72-Hour Delivery",
      description: "Get your report within 72 hours of inspection. We understand cooling-off periods are tight and timing matters."
    }
  ];

  const whatWeInspect = [
    "Roof and guttering condition",
    "External walls and cladding",
    "Windows and doors",
    "Internal walls and ceilings",
    "Flooring throughout",
    "Wet areas (bathrooms, laundry, kitchen)",
    "Electrical visible condition",
    "Plumbing visible condition",
    "Subfloor area (where accessible)",
    "Roof void (where accessible)",
    "Decks, verandahs, carports",
    "Fencing and boundary structures"
  ];

  const faqs = [
    {
      question: "When should I book a pre-purchase inspection?",
      answer: "Book as soon as you're seriously considering a property. Ideally, have the inspection done during the cooling-off period so you can withdraw if major issues are found. We offer fast turnaround to meet your timeline."
    },
    {
      question: "What if the inspection finds major problems?",
      answer: "Our report clearly documents any issues with photos. You can use this information to negotiate with the seller, request repairs, adjust your offer, or in serious cases, withdraw from the purchase during the cooling-off period."
    },
    {
      question: "Do you provide structural engineering advice?",
      answer: "We focus on visual inspections only — no structural opinions or grey areas. If we identify visible issues that may require further investigation, we'll note this in the report so you can engage a specialist if needed."
    },
    {
      question: "Can I attend the inspection?",
      answer: "Absolutely! We encourage buyers to attend. It's a great opportunity to learn about the property firsthand and ask questions. We'll walk you through our findings on-site."
    },
    {
      question: "What's included in the price?",
      answer: "Our flat-rate pricing (pre-purchase building inspections from $455 incl. GST) covers the full visual inspection and detailed digital report with photos. No hidden extras or surprise fees. Building condition reports also include our complimentary remediation report with cost estimates."
    },
    {
      question: "Do you inspect for termites and pests?",
      answer: "Our visual inspection includes looking for visible signs of pest activity. For a dedicated pest and termite inspection, we can arrange this through our trusted partner network."
    }
  ];

  const services = [
    { name: "Building Condition Reports", path: "/services/building-condition", description: "Comprehensive visual assessment with remediation estimates" },
    { name: "Building Status Reports", path: "/services/building-status", description: "Approval and compliance verification" },
    { name: "Pool Reports", path: "/services/pool", description: "Pool barrier and safety compliance" },
    { name: "Plumbing Reports", path: "/services/plumbing", description: "Full visual plumbing inspection" },
  ];

  const buyerSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Pre-Purchase Building Inspections for Home Buyers",
    "description": "Professional pre-purchase building inspections helping Darwin home buyers make informed decisions. Visual condition reports with 72-hour delivery.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Aussie Inspections",
      "telephone": "+61413245519",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Darwin",
        "addressRegion": "NT",
        "addressCountry": "AU"
      }
    },
    "areaServed": {
      "@type": "City",
      "name": "Darwin"
    },
    "offers": {
      "@type": "Offer",
      "price": "260",
      "priceCurrency": "AUD"
    }
  };

  return (
    <div>
      <SEO 
        title="Inspections for Home Buyers"
        description="Pre-purchase building inspections for Darwin home buyers. Get a clear picture of your potential property before you buy. Pre-purchase building inspections from $455 incl. GST with 72-hour report delivery."
        keywords="pre-purchase inspection Darwin, home buyer inspection, building inspection before buying, property inspection Darwin, first home buyer inspection"
        url="/inspections-for-buyers"
        schema={buyerSchema}
      />

      {/* CTA Banner */}
      <CTABanner />

      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center bg-slate-900">
        <div className="absolute inset-0">
          <img
            src="/assets/img/agent-and-client.jpg"
            alt="Home buyer inspecting property"
            className="w-full h-full object-cover opacity-30"
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="flex items-center space-x-2 text-secondary mb-4">
              <Home className="w-5 h-5" />
              <span className="font-semibold uppercase tracking-wider text-sm">For Home Buyers</span>
            </div>
            <h1 className="font-chivo text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none text-white mb-4">
              Buy with Confidence
            </h1>
            <p className="text-lg text-slate-200">
              Know exactly what you&apos;re buying. Our pre-purchase inspections give you the clarity you need to make an informed decision.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-4">
              Why Every Buyer Needs an Inspection
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Buying a home is likely the biggest purchase you&apos;ll ever make. Don&apos;t leave it to chance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {buyerBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-chivo text-lg font-bold text-slate-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Section */}
      <section className="py-12 bg-amber-50 border-y border-amber-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start space-x-4">
            <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-chivo text-xl font-bold text-slate-900 mb-2">
                Don&apos;t Skip the Inspection
              </h3>
              <p className="text-slate-700">
                Many buyers skip inspections to save money or speed up the process — and regret it later. Hidden issues like termite damage, structural problems, or moisture issues can cost tens of thousands to fix. An inspection from just $260 could save you from a costly mistake.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Inspect */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-6">
                What We Inspect
              </h2>
              <p className="text-slate-600 mb-8">
                Our comprehensive visual inspection covers all accessible areas of the property, documenting condition and identifying visible issues.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {whatWeInspect.map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="/assets/img/inspection-checklist.jpg"
                alt="Property inspection checklist"
                className="rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-primary text-white p-6 rounded-lg shadow-lg">
                <div className="text-3xl font-bold">72hr</div>
                <div className="text-blue-100 text-sm">Report Delivery</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services for Buyers */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-4 text-center">
            Inspection Services for Buyers
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto text-center mb-12">
            Choose the inspection that suits your needs. All reports delivered within 72 hours.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.path}
                className="group bg-slate-50 p-6 rounded-lg border border-slate-200 hover:border-primary hover:shadow-lg transition-all"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-chivo text-lg font-bold text-slate-900 group-hover:text-primary mb-2">
                      {service.name}
                    </h3>
                    <p className="text-slate-600 text-sm">{service.description}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left font-semibold text-slate-900">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-chivo text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Inspect Your Potential New Home?
          </h2>
          <p className="text-lg text-blue-100 mb-2">
            Book your pre-purchase inspection today
          </p>
          <p className="text-blue-100 mb-8">
            From $455 incl. GST • 72-hour report delivery • Flat-rate pricing
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-secondary hover:bg-secondary/90 text-white font-bold tracking-wide uppercase px-12"
            >
              <Link to="/contact">Book Inspection</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-white/30 hover:border-white hover:bg-white/10 text-white font-bold tracking-wide uppercase px-12"
            >
              <a href="tel:+61413245519" className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span>0413 24 55 19</span>
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
